/*
  Начисления из любой учётной системы - §3.3.2, §4.3.2.

  Выделено из `ImportPage` 18.09.2026, когда на экран добавился чек-лист
  готовности данных: страница упёрлась в потолок компонента, и выбор был между
  поднятием порога и разделением. Разделение совпало с границей: страница -
  контейнер вкладок и журнала, а каждая вкладка отвечает за свой вид файла и
  своё состояние. Три соседние вкладки так устроены с самого начала
  (`OneCUpload`, `IdentityUpload`, `PremisesUpload`), и эта была единственной,
  жившей прямо в странице.

  Между выбором файла и записью стоит шаг сопоставления колонок (08.09.2026). Он
  показывается всегда, даже когда всё распозналось само: человек должен видеть,
  что именно система приняла за сумму долга, ДО того как цифра уйдёт в
  заявление, а не после.

  Строки с проблемами загрузку не блокируют - они собираются в список «требует
  внимания» с объяснением по каждой.

  «Выбрать счета…» (решение владельца 16.10.2026) - тем же окном, что у реестра
  должников: не нажимали - грузится весь файл.

  🔴 С 19.10.2026 файл уходит на сервер ОДИН раз и разбирается один раз
  (указание владельца): выбор файла сразу отправляет его, разбор идёт фоновой
  работой с ходом, а сопоставление колонок, окно счетов и запись называют уже
  прочитанный файл (`useUploadFlow`). Запись - тоже фоновой работой со
  счётчиком строк; весь путь виден блоком «Ход загрузки».
*/

import { useEffect, useState } from 'react'

import { api } from '../api/client.js'
import { accounts, grouped, rows as rowsCount } from '../api/format.js'
import ColumnMapping from './ColumnMapping.jsx'
import { Notice } from './Common.jsx'
import ImportOutcome from './ImportOutcome.jsx'
import NameDifferences from './picker/NameDifferences.jsx'
import PickAccounts from './picker/PickAccounts.jsx'
import UploadStages from './upload/UploadStages.jsx'
import useUploadFlow from './upload/useUploadFlow.js'
import UploadHelp from './UploadHelp.jsx'

/*
  Образец таблицы начислений - он же файл под кнопкой «Скачать образец».

  Две строки одного счёта за один месяц по разным услугам: так виден главный
  признак этой выгрузки - строка не на счёт, а на услугу за месяц. Помесячная
  разбивка нужна не для порядка: у каждого начисления свой срок давности и свой
  период просрочки, и свернуть их в одну строку значит потерять и то, и другое.
*/
const CHARGES_SAMPLE = {
  file: 'начисления-образец.csv',
  headers: ['Лицевой счёт', 'Адрес', 'Услуга', 'Период', 'Начислено', 'Оплачено', 'Тип помещения'],
  rows: [
    ['20346', 'пгт Андреевка, д. 31А, кв. 29', 'Содержание жилья', '01.2026', '2 340,50', '0,00', 'Частная'],
    ['20346', 'пгт Андреевка, д. 31А, кв. 29', 'Отопление', '01.2026', '3 120,00', '1 000,00', 'Частная'],
  ],
}

export default function ChargesUpload({ dueDay, onLoaded, onEditDueDay }) {
  const flow = useUploadFlow('charges')
  const file = flow.stored
  const [preview, setPreview] = useState(null)
  const [choice, setChoice] = useState({})
  // Окно «Какие счета загрузить» (16.10.2026): счета файла по нынешнему
  // сопоставлению колонок и выбор человека; пусто - весь файл.
  const [choices, setChoices] = useState(null)
  const [only, setOnly] = useState(null)
  const [outcome, setOutcome] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  function reset() {
    setPreview(null)
    setChoice({})
    setChoices(null)
    setOnly(null)
    setOutcome(null)
    setError(null)
  }

  function choose(selected) {
    if (!selected) return
    reset()
    flow.choose(selected)
  }

  // Новый заход пересоздаёт поле выбора: в нём не остаётся имени прежнего файла.
  const [round, setRound] = useState(0)
  function again() {
    reset()
    flow.choose(null)
    setRound(round + 1)
  }

  // Файл прочитан - сразу сопоставление колонок: это и есть «Проверка строк».
  const { check } = flow
  const storedId = file?.storedId
  useEffect(() => {
    if (!storedId) return
    let alive = true
    check(
      () => api.previewImport({ storedId }),
      (seen) => `колонки распознаны, строк в файле: ${grouped(seen.rows_total)}`,
    )
      .then((understood) => {
        if (!alive) return
        setPreview(understood)
        setChoices(understood.choices)
        setChoice(
          Object.fromEntries(understood.fields.map((item) => [item.field, item.header ?? ''])),
        )
      })
      .catch((failure) => alive && setError(failure))
    return () => {
      alive = false
    }
  }, [storedId, check])

  // Другая колонка - другие счета: перечень для окна спрашивается заново при
  // открытии. Выбор снимается, только если сменилась колонка самого счёта.
  function remap(field, header) {
    setChoice({ ...choice, [field]: header })
    setChoices(null)
    if (field === 'account_number') setOnly(null)
  }

  async function need() {
    setError(null)
    try {
      setChoices((await api.previewImport(file, choice)).choices)
      return true
    } catch (failure) {
      setError(failure)
      return false
    }
  }

  // Подтверждения объёма больше нет (19.10.2026): сторож запись не останавливает.
  // Запись - фоновой работой по уже прочитанному файлу, с ходом в «Ход загрузки».
  async function upload() {
    setBusy(true)
    setError(null)
    try {
      const written = await flow.write('charges', {
        column_overrides: JSON.stringify(choice),
        only_accounts: only ? JSON.stringify(only) : null,
      })
      setOutcome(written)
      await onLoaded(written.import_run_id)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const missing = preview
    ? preview.fields.filter((item) => item.required && !choice[item.field])
    : []

  return (
    <>
      {/* Эта вкладка принимает таблицу любого биллинга, не только 1С: у 1С своя
          вкладка рядом, где отчёты названы поимённо. Поэтому памятка говорит не
          «нажмите вот здесь», а показывает, что должно получиться. */}
      <UploadHelp
        title="Какой файл сюда подходит"
        where={[
          'Помесячные начисления из вашей учётной системы: строка на услугу за месяц. ' +
            'Если учёт ведётся в 1С, возьмите соседнюю вкладку «Выгрузка из 1С» - там ' +
            'отчёты названы поимённо и разворачивать иерархию не нужно.',
          'Пять граф обязательны: лицевой счёт, адрес, услуга, месяц начисления и сумма. ' +
            'Больше ничего для загрузки не нужно.',
        ]}
        sample={CHARGES_SAMPLE}
        note={
          'Оплачено, тип помещения, площадь, телефон и собственник - необязательны, но ' +
          'экономят отдельную загрузку: тип помещения нужен для нового счёта, без него ' +
          'строка ложится в «требует внимания».'
        }
      />

      {/* Срок оплаты показан ДО выбора файла, а не после загрузки: от него
          считаются пени и исковая давность, и человек должен видеть, какую дату
          Арбитр ЖКХ применит, прежде чем цифра уйдёт в заявление. Ровно та же
          строка и тот же переход стоят на вкладке «Выгрузка из 1С» - правило
          одно, и выглядеть оно обязано одинаково (29.09.2026). */}
      <section className="section card">
        {/* Кнопка стоит ПОСЛЕ точки, отдельной строкой от пояснения: вставленная
            в середину фразы, она разрывает предложение переносом и читается как
            часть текста (осмотр в браузере 29.09.2026). */}
        <p className="onec__due">
          {dueDay ? (
            <>
              Срок оплаты: <b>{dueDay}-е число следующего месяца</b>.
            </>
          ) : (
            <>
              Срок оплаты <b>не задан</b>.
            </>
          )}{' '}
          <button type="button" className="button button--quiet" onClick={onEditDueDay}>
            Изменить
          </button>
        </p>
        <p className="muted">
          {dueDay
            ? 'Берётся из настроек организации для строк, где своей даты в файле нет.'
            : 'Начисления загрузятся и будут видны в портфеле, но пени и исковая давность ' +
              'по ним не посчитаются, и судебный пакет по таким счетам не соберётся.'}
        </p>
      </section>

      <section className="section card">
        {/* Пояснение снаружи подписи: внутри `<label>` оно вошло бы в название
            поля для читалки экрана - см. `Field` в `Common.jsx`. */}
        <div className="field field--l">
          <label className="field__control">
            <span className="field__label">Файл выгрузки</span>
            <input
              key={`${round}-${flow.attempt}`}
              className="field__file"
              type="file"
              accept=".csv,.xlsx"
              disabled={busy || flow.busy}
              onChange={(e) => choose(e.target.files?.[0])}
            />
          </label>
          <span className="field__hint">
            Строку с заголовками Арбитр ЖКХ найдёт сама: она не обязана быть самой верхней.
          </span>
        </div>
        <UploadStages flow={flow} checkHint="колонки распознаются сразу после разбора" />
        <Notice error={flow.error ?? error} />
      </section>

      {preview ? (
        <>
          <ColumnMapping
            preview={preview}
            choice={choice}
            disabled={busy}
            onChange={remap}
          />
          <section className="section row">
            <PickAccounts
              choices={choices}
              only={only}
              onChange={setOnly}
              onNeed={need}
              disabled={busy || missing.length > 0 || Boolean(outcome)}
              note={`${flow.file?.filename ?? ''} · счетов в файле: ${choices?.length ?? '…'}`}
              amountTitle="Долг по файлу"
            />
            <button
              type="button"
              className="button"
              disabled={busy || missing.length > 0 || Boolean(outcome)}
              onClick={upload}
            >
              {only
                ? `Загрузить выбранные: ${accounts(only.length)}`
                : `Загрузить ${rowsCount(preview.rows_total)}`}
            </button>
            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              onClick={again}
            >
              Выбрать другой файл
            </button>
          </section>
          {outcome ? null : <NameDifferences choices={choices} />}
        </>
      ) : null}

      {outcome ? (
        <ImportOutcome outcome={outcome} seconds={flow.written?.seconds} onAgain={again} />
      ) : null}
    </>
  )
}
